import { createSlice } from '@reduxjs/toolkit';
import { AiResponseSummaryDto } from '@kortxio/hub-api';
import {
  deleteAiResponseFeedback,
  getAiResponseDetail,
  getAiResponseSummaries,
  upsertAiResponseFeedback,
} from 'features/ai-responses/async';
import {
  applyOptimisticVote,
  applyOptimisticVoteToSummaryRow,
  clearVoteOnSummaryRow,
  clearVoteOptimistically,
  restoreSummaryRowVoteCounts,
  restoreVoteSnapshot,
} from 'features/ai-responses/helper';
import { AiResponsesState } from 'features/ai-responses/types';
import {
  fulfilled,
  initialRequestState,
  pending,
  rejected,
  requestReducer,
} from 'features/shared/request';

const patchSummaryRow = (
  state: AiResponsesState,
  id: string | undefined,
  patcher: (row: AiResponseSummaryDto) => AiResponseSummaryDto
): void => {
  if (!id || !state.aiResponses.data) {
    return;
  }

  const index = state.aiResponses.data.findIndex((row) => row.id === id);
  if (index === -1) {
    return;
  }

  state.aiResponses.data[index] = patcher(state.aiResponses.data[index]);
};

export const initialState: AiResponsesState = {
  aiResponses: {
    data: undefined,
    request: initialRequestState,
  },
  aiResponseDetail: {
    data: undefined,
    request: initialRequestState,
    feedbackRequest: initialRequestState,
  },
};

const slice = createSlice({
  name: 'aiResponses',
  initialState,
  reducers: {
    resetAiResponses(state) {
      state.aiResponses = {
        data: undefined,
        request: initialRequestState,
      };
    },
    resetAiResponseDetail(state) {
      state.aiResponseDetail = {
        data: undefined,
        request: initialRequestState,
        feedbackRequest: initialRequestState,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAiResponseSummaries.pending, (state, { meta }) => {
      state.aiResponses.data = undefined;
      state.aiResponses.request = requestReducer(
        state.aiResponses.request,
        pending({ meta })
      );
    });
    builder.addCase(
      getAiResponseSummaries.fulfilled,
      (state, { meta, payload }) => {
        if (state.aiResponses.request.id === meta.requestId) {
          state.aiResponses.data = payload;
          state.aiResponses.request = requestReducer(
            state.aiResponses.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getAiResponseSummaries.rejected, (state, { meta }) => {
      state.aiResponses.data = undefined;
      state.aiResponses.request = requestReducer(
        state.aiResponses.request,
        rejected({ meta })
      );
    });

    builder.addCase(getAiResponseDetail.pending, (state, { meta }) => {
      state.aiResponseDetail.data = undefined;
      state.aiResponseDetail.request = requestReducer(
        state.aiResponseDetail.request,
        pending({ meta })
      );
    });
    builder.addCase(
      getAiResponseDetail.fulfilled,
      (state, { meta, payload }) => {
        if (state.aiResponseDetail.request.id === meta.requestId) {
          state.aiResponseDetail.data = payload;
          state.aiResponseDetail.request = requestReducer(
            state.aiResponseDetail.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getAiResponseDetail.rejected, (state, { meta }) => {
      state.aiResponseDetail.data = undefined;
      state.aiResponseDetail.request = requestReducer(
        state.aiResponseDetail.request,
        rejected({ meta })
      );
    });

    builder.addCase(upsertAiResponseFeedback.pending, (state, { meta }) => {
      if (state.aiResponseDetail.data) {
        state.aiResponseDetail.data = applyOptimisticVote(
          state.aiResponseDetail.data,
          meta.arg.value
        );
      }

      patchSummaryRow(state, meta.arg.id, (row) =>
        applyOptimisticVoteToSummaryRow(
          row,
          meta.arg.value,
          meta.arg.previousFeedback
        )
      );

      state.aiResponseDetail.feedbackRequest = requestReducer(
        state.aiResponseDetail.feedbackRequest,
        pending({ meta })
      );
    });
    builder.addCase(upsertAiResponseFeedback.fulfilled, (state, { meta }) => {
      state.aiResponseDetail.feedbackRequest = requestReducer(
        state.aiResponseDetail.feedbackRequest,
        fulfilled({ meta })
      );
    });
    builder.addCase(upsertAiResponseFeedback.rejected, (state, { meta }) => {
      if (state.aiResponseDetail.data) {
        state.aiResponseDetail.data = restoreVoteSnapshot(
          state.aiResponseDetail.data,
          meta.arg.previousFeedback,
          meta.arg.previousThumbsUpCount,
          meta.arg.previousThumbsDownCount
        );
      }

      patchSummaryRow(state, meta.arg.id, (row) =>
        restoreSummaryRowVoteCounts(
          row,
          meta.arg.previousThumbsUpCount,
          meta.arg.previousThumbsDownCount
        )
      );

      state.aiResponseDetail.feedbackRequest = requestReducer(
        state.aiResponseDetail.feedbackRequest,
        rejected({ meta })
      );
    });

    builder.addCase(deleteAiResponseFeedback.pending, (state, { meta }) => {
      if (state.aiResponseDetail.data) {
        state.aiResponseDetail.data = clearVoteOptimistically(
          state.aiResponseDetail.data
        );
      }

      patchSummaryRow(state, meta.arg.id, (row) =>
        clearVoteOnSummaryRow(row, meta.arg.previousFeedback)
      );

      state.aiResponseDetail.feedbackRequest = requestReducer(
        state.aiResponseDetail.feedbackRequest,
        pending({ meta })
      );
    });
    builder.addCase(deleteAiResponseFeedback.fulfilled, (state, { meta }) => {
      state.aiResponseDetail.feedbackRequest = requestReducer(
        state.aiResponseDetail.feedbackRequest,
        fulfilled({ meta })
      );
    });
    builder.addCase(deleteAiResponseFeedback.rejected, (state, { meta }) => {
      if (state.aiResponseDetail.data) {
        state.aiResponseDetail.data = restoreVoteSnapshot(
          state.aiResponseDetail.data,
          meta.arg.previousFeedback,
          meta.arg.previousThumbsUpCount,
          meta.arg.previousThumbsDownCount
        );
      }

      patchSummaryRow(state, meta.arg.id, (row) =>
        restoreSummaryRowVoteCounts(
          row,
          meta.arg.previousThumbsUpCount,
          meta.arg.previousThumbsDownCount
        )
      );

      state.aiResponseDetail.feedbackRequest = requestReducer(
        state.aiResponseDetail.feedbackRequest,
        rejected({ meta })
      );
    });
  },
});

export const { resetAiResponses, resetAiResponseDetail } = slice.actions;

const { reducer } = slice;

export default reducer;
